import type { TFundingRecommendation, TReadinessCategory } from "./types";

function toRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== "object" || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

/**
 * Normalize one category from API shape (maturity + performance) to TReadinessCategory.
 */
export function normalizeApiCategory(apiCategory: unknown): TReadinessCategory {
  const raw = toRecord(apiCategory);
  if (!raw) return {};

  const maturity = toRecord(raw.maturity);
  const performance = toRecord(raw.performance);

  const maturityLevel = maturity?.level;
  const perfLevel = performance?.level as string | undefined;
  const score = typeof performance?.score === "number" ? performance.score : undefined;
  const reasoning = [performance?.reasoning, maturity?.reasoning].filter(Boolean).join("\n\n");
  const scoreExplanation = performance?.score_explanation as string | undefined;
  const confidence = (performance?.confidence ?? maturity?.confidence) as string | undefined;

  return {
    score,
    performance_level: perfLevel,
    maturity_level: typeof maturityLevel === "string" ? maturityLevel : undefined,
    confidence,
    reasoning: reasoning.length > 0 ? reasoning : undefined,
    score_explanation: scoreExplanation,
  };
}

function toStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value.filter((item): item is string => typeof item === "string");
}

/**
 * Normalize company_readiness.funding_recommendations from the readiness API
 * into TFundingRecommendation[]. Entries without a funding_type are dropped.
 */
export function normalizeFundingRecommendationsFromApi(value: unknown): TFundingRecommendation[] {
  if (!Array.isArray(value)) return [];

  const out: TFundingRecommendation[] = [];
  for (const entry of value) {
    const raw = toRecord(entry);
    if (!raw || typeof raw.funding_type !== "string" || raw.funding_type.trim().length === 0) continue;
    out.push({
      funding_type: raw.funding_type,
      suitability_level: typeof raw.suitability_level === "string" ? raw.suitability_level : undefined,
      suitability_score: typeof raw.suitability_score === "number" ? raw.suitability_score : undefined,
      assessment: typeof raw.assessment === "string" ? raw.assessment : undefined,
      reasoning: typeof raw.reasoning === "string" ? raw.reasoning : undefined,
      key_concerns: toStringArray(raw.key_concerns),
      key_strengths: toStringArray(raw.key_strengths),
    });
  }
  return out;
}

/**
 * Normalize company_readiness.categories from the readiness API into Record<string, TReadinessCategory>.
 */
export function normalizeReadinessCategoriesFromApi(apiCategories: unknown): Record<string, TReadinessCategory> {
  const cats = toRecord(apiCategories);
  if (!cats) return {};

  const out: Record<string, TReadinessCategory> = {};
  for (const [name, value] of Object.entries(cats)) {
    out[name] = normalizeApiCategory(value);
  }
  return out;
}
